import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	Section,
	StyleProps,
	ViewableObserver,
} from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { InternalLink } from "@hex-insights/router";
import { Icon } from "../../../../Components/Icon";
import { useHubPageInfo } from "../../../../Contexts";
import {
	Employee,
	EmployeeFilterInput,
	EmployeeNameDescriptionAndImageFragment,
	EmployeeOrder,
	EmployeeOrderField,
	EmployeeRole,
	mergeFilterInputs,
	OrderDirection,
	useEmployeeNamesDescriptionsAndImagesQuery,
} from "../../../../Utilities";
import {
	EmployeeFilterField,
	EmployeeFilterForm,
	EmployeeFilterFormState,
	EmployeeFilterFormUtils,
	EmployeeFilterFormValues,
} from "../../../Filter";
import { EmployeeIconNameDescription } from "../General";

export type ControlledEmployeesListModalProps = Omit<EmployeesListModalProps, "isLoading" | "employees"> & {
	filters?: EmployeeFilterInput;
	pageSize?: number;
};

export function ControlledEmployeesListModal({
	filters,
	groupBy,
	pageSize = 50,
	...props
}: ControlledEmployeesListModalProps) {
	let order: EmployeeOrder | undefined;
	if (groupBy === "role") {
		order = { field: EmployeeOrderField.Role, direction: OrderDirection.Desc };
	}

	const filterFormState = EmployeeFilterFormState.useFilterFormState();
	const filterInput = IndexForms.useFilterInput(filterFormState.formValues, EmployeeFilterFormUtils.toFilterInputs);

	const { loading, data, fetchMore } = useEmployeeNamesDescriptionsAndImagesQuery({
		variables: {
			filters: mergeFilterInputs(filterInput, filters),
			order,
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.employeeConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
				...previousQueryResult,
				employeeConnection: {
					...previousQueryResult.employeeConnection,
					pageInfo: fetchMoreResult.employeeConnection.pageInfo,
					edges: [...previousQueryResult.employeeConnection.edges, ...fetchMoreResult.employeeConnection.edges],
				},
			}),
		});
	}, [fetchMore, pageSize, endCursor]);
	const employees = React.useMemo(() => data?.employeeConnection.edges.map((e) => e.node), [data]);

	return (
		<EmployeesListModal
			isLoading={loading}
			employees={employees}
			filterFormState={filterFormState}
			groupBy={groupBy}
			hasMore={data?.employeeConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			{...props}
		/>
	);
}

export type EmployeesListModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	heading?: JSX.Element | string;
	isLoading?: boolean;
	employees: (Pick<Employee, "id"> & EmployeeNameDescriptionAndImageFragment)[] | null | undefined;
	filterFormState?: FormState<EmployeeFilterFormValues.FilterFormValues>;
	groupBy?: "role";
	hasMore?: boolean;
	loadMore?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function EmployeesListModal({
	heading = "Employees",
	employees,
	filterFormState,
	groupBy,
	hasMore = false,
	loadMore,
	ifRef,
	onClose,
	className,
	style,
}: EmployeesListModalProps) {
	const indexPage = useHubPageInfo("Employee", "index");

	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<Heading.H2 noMargin>{heading}</Heading.H2>

					{!!indexPage && (
						<InternalLink
							to={indexPage.to}
							className="link--primary link--no-text-decoration"
							style={{ fontSize: "0.875rem", marginLeft: "1rem", fontWeight: "bold" }}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								Go to Index <Icon.ArrowRight size={12} />
							</Row>
						</InternalLink>
					)}
				</Row>
			</Modal.Header>
			<Modal.Body>
				{!!filterFormState && (
					<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ marginBottom: "1.5rem" }}>
						<EmployeeFilterForm.Search formState={filterFormState} />
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<EmployeeFilterField.AcademicSubjectIDsInTooltipButton formState={filterFormState} />
							<EmployeeFilterField.ClassYearIDsInTooltipButton formState={filterFormState} />
						</Row>
					</Row>
				)}
				<Conditional>
					<If condition={groupBy === "role"}>
						<EmployeesListGroupByRoleContent employees={employees} />
					</If>
					<Else>
						<EmployeesListFlatContent employees={employees} />
					</Else>
				</Conditional>
				<If condition={hasMore && !!loadMore}>
					{!!loadMore && <ViewableObserver rootMargin="0px 0px 150px" onEnterView={loadMore} />}
				</If>
			</Modal.Body>
		</Modal>
	);
}

const employeeRoles: EmployeeRole[] = [
	EmployeeRole.Teacher,
	EmployeeRole.PrincipalAdmin,
	EmployeeRole.GeneralStaff,
	EmployeeRole.GeneralAdmin,
	EmployeeRole.FinancialAdmin,
	EmployeeRole.AcademicAdmin,
	EmployeeRole.FrontDesk,
	EmployeeRole.MedicalStaff,
];

const roleLabels: Record<EmployeeRole, string> = {
	[EmployeeRole.Teacher]: "Teachers",
	[EmployeeRole.PrincipalAdmin]: "Principal Administrators",
	[EmployeeRole.GeneralStaff]: "General Staff",
	[EmployeeRole.GeneralAdmin]: "General Administrators",
	[EmployeeRole.FinancialAdmin]: "Financial Team",
	[EmployeeRole.AcademicAdmin]: "Academic Administrators",
	[EmployeeRole.FrontDesk]: "Front Desk",
	[EmployeeRole.MedicalStaff]: "Medical Staff",
};

type EmployeesListGroupByRoleContentProps = {
	employees: EmployeesListModalProps["employees"];
};

function EmployeesListGroupByRoleContent({ employees }: EmployeesListGroupByRoleContentProps) {
	const employeesByRole = React.useMemo(() => {
		const employeesByRole: Partial<
			Record<EmployeeRole, Exclude<EmployeesListModalProps["employees"], null | undefined>>
		> = {};

		if (!employees) {
			return employeesByRole;
		}

		for (let i = 0; i < employees.length; i++) {
			const employee = employees[i];
			const key = employee.role;
			if (!employeesByRole[key]) {
				employeesByRole[key] = [];
			}
			employeesByRole[key]!.push(employee);
		}

		return employeesByRole;
	}, [employees]);

	return (
		<Column justify="spaced-start">
			{employeeRoles.map((role) => {
				const employees = employeesByRole[role] ?? [];

				if (employees.length === 0) {
					return null;
				}

				return (
					<Section key={role}>
						<Section.Header>
							<Heading.H3 noMargin>{roleLabels[role]}</Heading.H3>
						</Section.Header>
						<Section.Body>
							<EmployeesListFlatContent employees={employees} />
						</Section.Body>
					</Section>
				);
			})}
		</Column>
	);
}

const responsiveTracks = {
	0: 1,
	500: 2,
	1000: 3,
};

type EmployeesListFlatContentProps = {
	employees: EmployeesListModalProps["employees"];
};

function EmployeesListFlatContent({ employees }: EmployeesListFlatContentProps) {
	return (
		<Grid responsiveTracks={responsiveTracks} gap="1rem">
			{employees?.map((employee) => (
				<EmployeeIconNameDescription key={employee.id} employee={employee} style={{ width: "100%" }} />
			))}
		</Grid>
	);
}
